import * as React from 'react';

import * as Schema from 'nutshell-core/schema';
import {Session} from 'nutshell-core/session';
import type {Assignable} from 'nutshell-core/types';

import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import type {CustomOption} from 'shells/assignee-select/assignee-select';
import {Button} from 'shells/button/button';
import {Label} from 'shells/form/label';
import {FormattedNumberField} from 'shells/formatted-number-field/formatted-number-field';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';

import type {Comparator} from 'nutshell-graphql-types';

import {Checkbox} from '../fields/checkbox';
import {CollectionPicker} from '../fields/collection-picker';
import {ComparitorSelector} from '../fields/comparitor-selector';

import {getCollectionNameFromFieldName} from './get-collection-name-from-field-name';
import type {CriteriaField, UserAssignmentAutomation} from './user-assignments-types';

import subform from './subform-special-case.module.css';

import form from '../sales-process-form.module.css';

// @ts-expect-error
const trackJs = window.trackJs;
const UNASSIGN_OPTION: CustomOption = {id: 'UNASSIGN', name: 'Unassign lead'};

type Option = {
    value: string;
    label: string;
};

const fields = ['accountType', 'industry', 'market', 'products', 'sources', 'territory', 'value'];

type Props = {
    assignables: Assignable[];
    schema: Schema.Properties;
    onApplySpecialCase: (arg1: UserAssignmentAutomation) => void;
    onCancelSpecialCase: () => void;
};

type State = {
    assigneeId: string | null | undefined;
    shouldUnassign: boolean;
    shouldRoundRobin: boolean;
    criteriaField: string | null | undefined;
    criteriaCollection: CriteriaField | null | undefined;
    criteriaValue: string | null | undefined;
    valueComparator: Comparator;
};

export class SubformSpecialCase extends React.PureComponent<Props, State> {
    options: Option[];

    constructor(props: Props) {
        super(props);
        this.state = {
            assigneeId: null,
            shouldUnassign: false,
            shouldRoundRobin: false,
            criteriaField: null,
            criteriaCollection: null,
            criteriaValue: null,
            valueComparator: 'EQUAL',
        };
        this.options = createOptions(fields, props.schema);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // Just in case the schemas weren't fully loaded when the component first
        // mounted, update our options based on the new schema
        if (nextProps.schema !== this.props.schema) {
            if (trackJs) {
                trackJs.track('SubformSpecialCase: schema changed after component was mounted.');
            }

            this.options = createOptions(fields, nextProps.schema);
        }
    }

    render() {
        if (!NutClientConfig.hasTerritories) {
            this.options = this.options.filter((option) => option.value !== 'territory');
        }

        return (
            <div className={subform['special-cases']}>
                <Label htmlFor='SpecialCaseAssigneePicker'>Assign the lead to…</Label>
                <AssigneeSelect
                    inputId='SpecialCaseAssigneePicker'
                    onChange={this.handleAssigneeChange}
                    value={this.state.assigneeId}
                    options={this.props.assignables}
                    customOptions={[UNASSIGN_OPTION]}
                />
                {this.shouldShowRoundRobin() ? (
                    <div className={subform['round-robin']}>
                        <Checkbox
                            label='Round-robin'
                            checked={this.state.shouldRoundRobin}
                            onChange={this.handleRoundRobinChange}
                        />
                    </div>
                ) : null}
                <Label htmlFor='SpecialCaseCriteriaPicker'>If…</Label>
                <SelectPickerGeneric
                    inputId='SpecialCaseCriteriaPicker'
                    placeholder='- criteria -'
                    openMenuOnFocus={true}
                    menuShouldScrollIntoView={true}
                    value={this.options.filter(({value}) => value === this.state.criteriaField)}
                    onChange={this.handleCriteriaChange}
                    options={this.options}
                />
                {this.state.criteriaField === 'value' ? (
                    <div className={form['nested-label']}>
                        <ComparitorSelector
                            comparitorValue={this.state.valueComparator}
                            onChange={this.handleValueComparatorChange}
                        />
                        <FormattedNumberField
                            value={Number(this.state.criteriaValue)}
                            onEnter={this.handleFormattedNumberFieldReturn}
                            onChange={this.handleValueChange}
                        />
                    </div>
                ) : (
                    <div className={form['value-container']}>
                        <Label htmlFor='SpecialCaseCriteriaValueType'>Is</Label>
                        <CollectionPicker
                            value={this.state.criteriaValue}
                            isDisabled={!this.state.criteriaCollection}
                            collectionType={this.state.criteriaCollection}
                            onChange={this.handleCriteriaValueChange}
                            openMenuOnFocus={true}
                            inputId='SpecialCaseCriteriaValueType'
                        />
                    </div>
                )}
                <div className={subform.buttons}>
                    <Button size='big' onClick={this.handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant='save'
                        size='big'
                        disabled={!this.isValid()}
                        onClick={this.handleApply}
                    >
                        Apply
                    </Button>
                </div>
            </div>
        );
    }

    getCurrentAssignee() {
        return this.props.assignables.find((opt) => opt.id === this.state.assigneeId);
    }

    shouldShowRoundRobin() {
        const assignee = this.getCurrentAssignee();
        if (!assignee) return false;

        return assignee.type === 'teams';
    }

    handleRoundRobinChange = (shouldRoundRobin: boolean) => {
        this.setState({shouldRoundRobin});
    };

    handleAssigneeChange = (assigneeId?: string | null) => {
        if (assigneeId === UNASSIGN_OPTION.id) {
            this.handleUnassignSelected();
        } else {
            this.setState({
                shouldUnassign: false,
                assigneeId,
            });
        }
    };

    handleUnassignSelected = () => {
        this.setState({
            assigneeId: UNASSIGN_OPTION.id,
            shouldUnassign: true,
        });
    };

    handleCriteriaChange = (option?: Option | null) => {
        const fieldName = option && option.value;
        const collectionName = getCollectionNameFromFieldName(fieldName, this.props.schema);
        this.setState({
            criteriaField: fieldName,
            criteriaCollection: fieldName === 'value' ? null : collectionName,
            valueComparator: fieldName === 'value' ? 'GREATER' : 'EQUAL',
            // Set the initial value to 0 so that `isValid` works correctly with
            // the inital value
            criteriaValue: fieldName === 'value' ? '0' : null,
        });
    };

    handleCriteriaValueChange = (option: Option) => {
        this.setState({criteriaValue: option.value});
    };

    handleValueComparatorChange = (option: {value: Comparator}) => {
        this.setState({valueComparator: option && option.value});
    };

    handleValueChange = (value?: number | null) => {
        this.setState({criteriaValue: String(value)});
    };

    handleApply = () => {
        const {criteriaField, valueComparator, criteriaValue} = this.state;
        // Can't seem to use this.isValid for flow  :(
        if (
            (this.state.assigneeId || this.state.shouldUnassign) &&
            criteriaField &&
            criteriaValue
        ) {
            const assignee = this.getCurrentAssignee();
            let assigneeToApply = null;

            if (assignee && assignee.type === 'users') {
                assigneeToApply = {
                    id: assignee.id,
                    name: assignee.name,
                    isEnabled: assignee.isEnabled,
                    // we're just doing this to satisfy flow, it gets thrown away
                    __typename: 'User',
                };
            }

            if (assignee && assignee.type === 'teams') {
                assigneeToApply = {
                    id: assignee.id,
                    name: assignee.name,
                    deletedTime: assignee.deletedTime,
                    // we're just doing this to satisfy flow, it gets thrown away
                    __typename: 'Team',
                };
            }

            this.props.onApplySpecialCase({
                // @ts-expect-error
                assignee: assigneeToApply,
                shouldUnassign: this.state.shouldUnassign,
                shouldRoundRobin: this.state.shouldRoundRobin,
                // @ts-expect-error
                criteria: formatCriteria(criteriaField, valueComparator, criteriaValue),
            });
        }
    };

    isValid() {
        return Boolean(
            (this.state.assigneeId || this.state.shouldUnassign) &&
                this.state.criteriaField &&
                this.state.criteriaValue
        );
    }

    handleCancel = () => {
        this.props.onCancelSpecialCase();
    };

    handleFormattedNumberFieldReturn = (e: React.KeyboardEvent) => {
        e.preventDefault();

        if (this.isValid()) {
            this.handleApply();
        }
    };
}

function formatCriteria(field: string, comparator: Comparator, value: string) {
    if (field === 'value') {
        return {
            __typename: 'EntityFilterFieldConfigCurrency',
            fieldName: field,
            fieldType: 'CURRENCY',
            currencyComparator: comparator,
            currencyValue: Number(value),
            currencyCode: Session.getInstanceDefaultCurrencyCode(),
        };
    }

    return {
        __typename: 'EntityFilterFieldConfigRelationship',
        fieldName: field,
        fieldType: 'RELATIONSHIP',
        relationshipData: [value],
        relationshipAnyAll: 'ANY',
    };
}

/*
 * Given a list of options which are fieldnames, and a schema, return
 * a list of options that we can render in a picker list.
 */
// @ts-expect-error
function createOptions(optionList: string[], schema) {
    return optionList.map((type) => {
        // If we haven't loaded the schema yet, just use the ugly names
        if (!schema[type]) {
            return {value: type, label: type};
        }

        return {value: type, label: schema[type].title};
    });
}
